/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useSelector, useDispatch } from 'react-redux';
import {
  selectPreviousYearGroupRoomConsumptionDetails,
  selectPreviousYearGroupByRatesDetails,
  selectGroupPreviousYearForecastDetails,
  selectHotelDetails,
  selectPreviousYearDate,
} from 'modules/rate-calendar/selectors';
import { rateCalenderActions } from 'modules/rate-calendar/slice';
import { useEffect } from 'react';
import { calculateForecastTotalValue } from 'modules/common/utils/calculate-forecast-total';
/**
 * Custom hook for metrics table is defined here
 * @param {*} event
 */
const useMetrics = (event) => {
  //
  const dispatch = useDispatch();
  //
  const prevYearGroupRoomConsumptionDetails = useSelector(
    selectPreviousYearGroupRoomConsumptionDetails
  );
  const prevYearGroupByRatesDetails = useSelector(selectPreviousYearGroupByRatesDetails);
  const prevYearForecastDataGroupedByDate = useSelector(selectGroupPreviousYearForecastDetails);
  const hotelData = useSelector(selectHotelDetails);
  const selectedDate = useSelector(selectPreviousYearDate);
  //
  useEffect(() => {
    const prevYearForecastTotalList = calculateForecastTotalValue(prevYearGroupByRatesDetails);
    const prevYearRoomRate = prevYearGroupByRatesDetails[selectedDate];
    const prevYearRoomConsumption = prevYearGroupRoomConsumptionDetails?.[selectedDate];
    const prevYearForecast = prevYearForecastTotalList[selectedDate];
    const prevYearGroupByForecast = prevYearForecastDataGroupedByDate?.[selectedDate];
    //
    const prevYearAvailableRooms =
      (hotelData?.roomCapacity ?? 0) -
      (prevYearGroupByForecast ? prevYearGroupByForecast[0]?.blocked ?? 0 : 0);
    const leadRate = prevYearRoomRate
      ? `$${prevYearRoomRate
          ?.filter((segment) => segment?.marketSegment === 'retail')[0]
          ?.rate?.toLocaleString('en')}`
      : '';
    const HumanForecast = prevYearForecast
      ? `$${prevYearForecast?.forecastTotal?.toLocaleString('en')}`
      : '';
    const adr = prevYearRoomConsumption
      ? `$${prevYearRoomConsumption?.[0]?.adr?.toLocaleString('en')}`
      : '';
    const sellLimit = selectedDate ? `${prevYearAvailableRooms}` : '';
    const roomAvailability = selectedDate
      ? prevYearRoomConsumption && prevYearGroupByForecast
        ? `${
            (prevYearRoomConsumption?.[0]?.availableRoomCount ?? 0) -
            (prevYearGroupByForecast[0]?.blocked ?? 0)
          }`
        : `${prevYearAvailableRooms}`
      : '';
    const occupancyPercentage = prevYearRoomConsumption
      ? `${(
          ((prevYearRoomConsumption?.[0]?.occupiedRoomCount ?? 0) / (prevYearAvailableRooms ?? 1)) *
          100
        ).toFixed(3)}%`
      : '';
    const blockedRoom = prevYearGroupByForecast ? prevYearGroupByForecast[0]?.blocked : '';
    const revenue = prevYearRoomConsumption
      ? `$${(
          (prevYearRoomConsumption?.[0]?.adr ?? 0) *
          (prevYearRoomConsumption?.[0]?.occupiedRoomCount ?? 0)
        )?.toLocaleString('en')}`
      : '';
    const revPar = prevYearRoomConsumption
      ? `$${(
          Math.round(
            (prevYearRoomConsumption?.[0]?.adr ?? 0) *
              (prevYearRoomConsumption?.[0]?.occupiedRoomCount ?? 0)
          ) / (prevYearAvailableRooms ?? 1)
        ).toFixed(2)}`
      : '';
    //
    const metricsTable = [
      {
        id: '1',
        metrics: 'Lead Rate',
        value: selectedDate ? event?.middleBand?.title ?? '' : '',
        previousValue: selectedDate ? leadRate ?? '' : '',
      },
      {
        id: '2',
        metrics: 'Human Forecast',
        value: selectedDate ? event?.footer?.titleSecond ?? '' : '',
        previousValue: selectedDate ? HumanForecast ?? '' : '',
      },
      {
        id: '3',
        metrics: 'ADR',
        value: selectedDate ? event?.footer?.titleFirst ?? '' : '',
        previousValue: selectedDate ? adr ?? '' : '',
      },
      {
        id: '4',
        metrics: 'Sell Limit',
        value: selectedDate ? event?.topHeader?.title?.available ?? '' : '',
        previousValue: selectedDate ? sellLimit ?? '' : '',
      },
      {
        id: '5',
        metrics: 'Room Availability',
        value: selectedDate ? event?.topHeader?.title?.remaining ?? '' : '',
        previousValue: selectedDate ? roomAvailability ?? '' : '',
      },
      {
        id: '6',
        metrics: 'Occupancy Rate',
        value: selectedDate ? event?.occupancyPercentage ?? '' : '',
        previousValue: selectedDate ? occupancyPercentage ?? '' : '',
      },
      {
        id: '7',
        metrics: 'Blocked Rooms',
        value: selectedDate ? event?.blockedRoom ?? '' : '',
        previousValue: selectedDate ? blockedRoom ?? '' : '',
      },
      {
        id: '8',
        metrics: 'Revenue',
        value: selectedDate ? event?.revenue ?? '' : '',
        previousValue: selectedDate ? revenue ?? '' : '',
      },
      {
        id: '9',
        metrics: 'RevPAR',
        value: selectedDate && event ? `$${Math.round(event.revPar * 100) / 100}` ?? '' : '',
        previousValue: selectedDate ? revPar ?? '' : '',
      },
    ];
    dispatch(rateCalenderActions.setMetricsRows(metricsTable));
  }, [
    hotelData,
    selectedDate,
    prevYearForecastDataGroupedByDate,
    prevYearGroupByRatesDetails,
    prevYearGroupRoomConsumptionDetails,
  ]);
};
//
export default useMetrics;
